import React, { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import _ from 'lodash';

import { Form } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { ICoHost } from 'api/co-hosts';

import { Typography, Checkbox, Modal } from 'ui-kit';
import InputPhoneMask from 'components/Form/InputPhoneMask';
import { Input } from 'components/Form';

import {
  EMPTY_PHONE_NUMBER_REGEX,
  formatPhoneNumber,
  PHONE_NUMBER_REGEX,
  validateAndFormatPhoneNumber,
} from 'components/Form/InputPhoneMask/utils';

import './CoHostModal.scss';

interface ICoHostModal {
  isOpen: boolean;
  onConfirm: ({ data }: { data: ICoHost }) => void;
  onCancel: () => void;
  initialData?: ICoHost;
}

const validCoHostSchema = yup.object({
  email: yup.string().email('Please input a valid email.').required('Email address required.'),
  phone_number: yup
    .string()
    .nullable()
    .test('valid-phone-number', 'Please input a valid phone number.', (value) => {
      if (
        _.isEmpty(value) ||
        value.match(EMPTY_PHONE_NUMBER_REGEX) ||
        !_.isNull(validateAndFormatPhoneNumber({ phoneNumber: value }))
      ) {
        return true;
      }

      return PHONE_NUMBER_REGEX.test(value);
    }),
});

const CoHostModal = ({ isOpen, onConfirm, onCancel, initialData }: ICoHostModal) => {
  const { control, getValues, reset, handleSubmit } = useForm<ICoHost>({
    mode: 'all',
    defaultValues: {
      id: null,
      email: '',
      phone_number: '',
      is_request_sms_enabled: false,
      is_sales_sms_enabled: false,
      is_marketing_sms_enabled: false,
    },
    resolver: yupResolver(validCoHostSchema),
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
    } else if (isOpen && initialData) {
      reset(initialData);
    } else {
      reset({
        id: null,
        email: '',
        phone_number: '',
        is_request_sms_enabled: false,
        is_sales_sms_enabled: false,
        is_marketing_sms_enabled: false,
      });
    }
  }, [isOpen, reset]);

  const modalTitle = useMemo(() => {
    return initialData ? 'Edit Co-host Contact' : 'Invite Co-host';
  }, [initialData]);

  const handleConfirm = useCallback(() => {
    const data = getValues();

    const formattedData = {
      ...data,
      phone_number: formatPhoneNumber({ phone: data.phone_number }),
    };

    onConfirm({ data: formattedData });
  }, [onConfirm, getValues]);

  const handleCancel = useCallback(() => {
    onCancel();
    reset();
  }, [onCancel, reset]);

  return (
    <Modal
      className="CoHostModal"
      visible={isOpen}
      onOk={handleSubmit(handleConfirm)}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      okType="primary"
      destroyOnClose
      okButtonProps={{
        htmlType: 'submit',
      }}
    >
      <Typography className="CoHostModal_title" variant="h6">
        {modalTitle}
      </Typography>

      <Form layout="vertical">
        <Controller
          name="id"
          control={control}
          render={({ field: { value } }) => <input type="hidden" value={value} />}
        />
        <Controller
          name="email"
          control={control}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <Form.Item
              required
              name={name}
              label="Email address"
              className="email-label"
              tooltip={{
                title: 'Emails include request and approve, sales, service info, and reminders.',
                icon: <ExclamationCircleOutlined />,
              }}
              validateStatus={error ? 'error' : ''}
              help={error?.message}
              initialValue={value}
            >
              <Input
                className=""
                defaultValue={value}
                onChange={onChange}
                placeholder="email address..."
                disabled={initialData}
              />
            </Form.Item>
          )}
        />

        <Controller
          name="phone_number"
          control={control}
          render={({ field: { name, value, onChange, onBlur }, fieldState: { error } }) => (
            <Form.Item
              name={name}
              label="Phone number for text messages"
              className="phone-number-label"
              tooltip={{
                title: 'Available in the US and Canada.',
                icon: <ExclamationCircleOutlined />,
              }}
              validateStatus={error ? 'error' : ''}
              help={error ? error?.message : null}
              initialValue={value}
            >
              <InputPhoneMask onBlur={onBlur} onChange={onChange} value={value} />
            </Form.Item>
          )}
        />
        <div className="CoHostModal_list_notifications">
          <p className="label-title">Enable text notifications for:</p>
          <Controller
            name="is_request_sms_enabled"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <Form.Item name={name}>
                <Checkbox onChange={onChange} checked={value}>
                  Request and Approve: Request, Approve, Deny, Cancelled, Expired
                </Checkbox>
              </Form.Item>
            )}
          />
          <Controller
            name="is_sales_sms_enabled"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <Form.Item name={name}>
                <Checkbox onChange={onChange} checked={value}>
                  Sales: Purchase, Cancellation, Service Reminders, Inventory
                </Checkbox>
              </Form.Item>
            )}
          />
          <Controller
            name="is_marketing_sms_enabled"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <Form.Item name={name}>
                <Checkbox onChange={onChange} checked={value}>
                  News and Feature Updates
                </Checkbox>
              </Form.Item>
            )}
          />
        </div>
      </Form>
    </Modal>
  );
};
export default CoHostModal;
